import { is, List } from "immutable";
import * as QueryString from "querystringify";
import * as React from "react";
import * as BS from "react-bootstrap";
import * as BT from "react-bootstrap-table";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import * as M from "../../../models";
import { AppState } from "../../../store";
import { Utility } from "../../../utils";

type DataRow = M.Email.EmailTemplate & {
    endDateValue: number;
    startDateValue: number;
    status: string;
};

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultListViewStatePropsMap(state, ownProps),
    templates: state.adminEmailTemplates.templates,
    loadingTemplates: state.adminEmailTemplates.loadingTemplates,
    loadingTypes: state.adminEmailTemplates.loadingTypes,
    types: state.adminEmailTemplates.types,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadTemplates: () => dispatch(Actions.AdminEmailTemplate.loadTemplates()),
    loadTypes: () => dispatch(Actions.AdminEmailTemplate.loadTypes()),
    removeTemplate: (template: M.Email.EmailTemplate) => dispatch(Actions.AdminEmailTemplate.removeTemplate(template)),
    toggleLoadingTemplates: (loading: boolean) => dispatch(Actions.AdminEmailTemplate.toggleLoadingTemplates(loading)),
    toggleLoadingTypes: (loading: boolean) => dispatch(Actions.AdminEmailTemplate.toggleLoadingTypes(loading)),
});

type OwnProps = RouteComponentProps<{}> & {
};

type ConnectedState = Comp.Base.DefaultListViewConnectedState & {
    templates: List<M.Email.EmailTemplate>;
    loadingTemplates: boolean;
    loadingTypes: boolean;
    types: List<M.Email.EmailTemplateType>;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadTemplates: () => void;
    loadTypes: () => void;
    removeTemplate: (template: M.Email.EmailTemplate) => void;
    toggleLoadingTemplates: (loading: boolean) => void;
    toggleLoadingTypes: (loading: boolean) => void;
};

interface OwnState {
    appliedFilters: M.Search.AppliedFilter[];
    doFilter: boolean;
    filteredData: List<M.Email.EmailTemplate>;
    types: M.Search.SearchFilter[];
}

interface UrlQuery {
    type?: string;
}

type FilterType = "type";

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class ListComponent extends Comp.Base.BaseListViewComponent<CombinedProps, OwnState> {

    public constructor(props: any) {
        super(props);

        this.state = {
            ...this.getInitialListViewState(props),
            appliedFilters: [],
            doFilter: false,
            filteredData: List(),
            types: [],
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        await this.initializeComponent();
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (!is(prevProps.templates, this.props.templates))
            this.loadTemplatesFromState();

        if (!is(prevProps.types, this.props.types))
            this.loadTypesFromState();

    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public shouldComponentUpdate?(nextProps: Readonly<OwnProps>, nextState: Readonly<OwnState>, nextContext: any): boolean {
        if (nextState.doFilter) {
            this.filterData();
            return false;
        }

        if (nextProps.location.search !== this.props.location.search) {
            this.updateAllFilterChecks(nextProps.location.search);
            return false;
        }

        return true;
    }

    public render() {
        const loading = this.state.loading || this.props.loadingTemplates || this.props.loadingTypes;

        const actionsPrimary = <BS.ButtonToolbar>
            <BS.ButtonGroup>
                <Link className="btn btn-default" to={Paths.AdminEmailTemplateEdit(0)}>
                    <Comp.Generic.Icon icon="plus" /> Add new template
                </Link>
            </BS.ButtonGroup>
        </BS.ButtonToolbar>;

        const tableActions = this.getTableActions({
            dataLength: this.props.templates.size,
            filteredLength: this.state.filteredData.size,
            searchPlaceholder: "Search templates...",
        });

        return <Comp.Page.AdminPage
                actionsPrimary={actionsPrimary}
                actionsSecondary={tableActions}
                mainClass="results"
                title={"Email templates"}
            >

            <Comp.Generic.LoadingSpinner show={loading} />

            {this.getErrorAlert()}

            <div className="results-filters">
                <h3>Filters</h3>

                <Comp.Search.SearchFilterCategory
                    // defaultSort="name"
                    // excludeZeros
                    filters={this.state.types}
                    onChange={(id, checked) => this.onFilterCheck(id, checked, "type")}
                    showAll
                    title="Type"
                />
            </div>

            <div className="results-list">
                <Comp.Search.AppliedFilters
                    filters={this.state.appliedFilters}
                    onClearAll={() => this.clearFilters()}
                    onClearFilter={(id, type) => this.onFilterCheck(id, false, type as FilterType)}
                />

                <BT.BootstrapTable
                    bordered={false}
                    data={this.state.filteredData.toArray()}
                    pagination={this.state.tablePageSize > -1}
                    options={{
                        afterSearch: (search, results) => this.onTableSearch(search, results, () => this.afterSearch(search, results)),
                        page: this.state.tablePage,
                        paginationPanel: () => <div />,
                        sizePerPage: this.state.tablePageSize,
                        onSortChange: (sortName: any, sortOrder: any) => this.handleTableSort(sortOrder, sortName),
                        defaultSortName: "name",
                        defaultSortOrder: "asc",
                    }}
                    ref={(ref: any) => this.table = ref}
                >
                    <BT.TableHeaderColumn dataField="id" isKey width="5%">ID</BT.TableHeaderColumn>
                    <BT.TableHeaderColumn dataField="name" dataSort>Name</BT.TableHeaderColumn>
                    <BT.TableHeaderColumn dataField="type" dataSort width="15%">Type</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        dataField="id"
                        dataFormat={(cell: any, row: DataRow) => <Link to={Paths.AdminEmailTemplateEdit(row.id)}>Edit</Link>}
                        dataSort
                        width="100px"
                    />
                </BT.BootstrapTable>
            </div>

        </Comp.Page.AdminPage>;
    }

    private async afterSearch(search: string, results: ReadonlyArray<M.Email.EmailTemplate>) {
        if (search) {
            await this.updateFilterCounts();
        }
        else {
            await this.filterData();
            await this.updateFilterCounts();
        }
    }

    private clearFilters() {
        this.props.replaceUrlQuery({
            type: "",
        });
        this.saveTableState();
    }

    private async filterData() {
        const filteredData: M.Email.EmailTemplate[] = [];
        const urlQuery = QueryString.parse(this.props.location.search) as UrlQuery;

        // setup id arrays from url query
        const typeIds = (urlQuery.type || "").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10));

        const list = this.state.tableSearchResults
            ? List(this.state.tableSearchResults)
            : this.props.templates;

        list.forEach((x: M.Email.EmailTemplate) => {
            const isFiltered = !typeIds.length || typeIds.indexOf(x.typeId) > -1;

            if (isFiltered)
                filteredData.push(x);
        });

        await this.setStateAsync({
            ...this.state,
            doFilter: false,
            filteredData: List(filteredData),
        });
    }

    private getFilterTypeName(type: FilterType) {
        switch (type) {
            case "type": return "Type";
            default: return "Unknown";
        }
    }

    private async initializeComponent() {
        await this.loadTypesFromState(true);
        await this.loadTemplatesFromState(true);
        await this.updateAllFilterChecks(this.props.location.search);

        this.props.loadTemplates();
        this.props.loadTypes();
    }

    private async loadTypesFromState(toggleLoading: boolean = false) {
        const types: M.Search.SearchFilter[] = this.props.types
            .map((x: M.Email.EmailTemplateType) => ({
                checked: false,
                count: 0,
                id: x.id,
                name: x.name,
            }))
            .toArray();

        if (!this.props.types.size && toggleLoading)
            this.props.toggleLoadingTypes(true);

        await this.setStateAsync({ ...this.state, types });
        await this.updateAllFilterChecks();
    }

    private async loadTemplatesFromState(toggleLoading: boolean = false) {
        const templates = this.props.templates.toList();

        // await this.setStateAsync({ ...this.state, templates });

        if (!templates.size && toggleLoading)
            this.props.toggleLoadingTemplates(true);

        // update table state from location state if available
        this.updateTableState(templates);
        await this.filterData();
    }

    private async onFilterCheck(id: string | number, checked: boolean, type: FilterType) {
        const urlQuery = QueryString.parse(this.props.location.search);
        const filterIds = Utility.commaStringToIntArray(urlQuery[type]);
        Utility.addToOrRemoveFromArray(filterIds, id, checked);
        this.props.replaceUrlQuery({ [type]: filterIds.join(",") });
        this.saveTableState();
    }

    private async updateAllFilterChecks(query?: string) {
        // get filtering maps from state
        const types = [ ...this.state.types ];
        const appliedFilters: M.Search.AppliedFilter[] = [];

        this.updateFilterChecks(types, "type", appliedFilters, query);

        await this.setStateAsync({
            ...this.state,
            appliedFilters,
            types,
        });

        await this.setStateAsync({
            ...this.state,
            doFilter: true,
        });

        await this.updateFilterCounts();
    }

    private updateFilterChecks(filters: M.Search.SearchFilter[], type: FilterType, appliedFilters: M.Search.AppliedFilter[], query?: string) {
        query = query === undefined ? this.props.location.search : query;
        const urlQuery = QueryString.parse(query);
        const itemIds = Utility.commaStringToIntArray(urlQuery[type]);

        for (const item of filters) {
            item.checked = itemIds.indexOf(item.id) > -1;
            if (item.checked) {
                appliedFilters.push({ id: item.id, name: item.name, type, typeName: this.getFilterTypeName(type) });
            }
        }
    }

    private async updateFilterCounts(forFilters?: FilterType[]) {
        // get filtering maps from state
        const types = [ ...this.state.types ];

        const list = this.state.tableSearchResults ? List(this.state.tableSearchResults) : this.props.templates;

        if (!forFilters || forFilters.indexOf("type") > -1)
            for (const f of types)
                f.count = list.filter((x: M.Email.EmailTemplate) => f.id === x.typeId).size;

        await this.setStateAsync({
            ...this.state,
            types,
        });
    }
}

export const ListEmailTemplates: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(ListComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/adminEmailTemplates/list/List.tsx